/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplateCatalogPane.tsx
 * FILE_ID: QA-TEMPLATES-CATALOG-PANE-001
 * PURPOSE: Search plus location-type groups for the left picker column.
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { joinProductNames } from '../../../qa/qaProductResolve'
import type { QaTemplatePicker } from './useQaTemplatePicker'

export function QaTemplateCatalogPane({
  picker,
  collapsible = false,
}: {
  picker: QaTemplatePicker
  collapsible?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = !collapsible || expanded
  return (
    <div className="flex min-h-0 flex-col">
      <input
        value={picker.query}
        onChange={(event) => picker.setQuery(event.target.value)}
        placeholder="Search templates and steps"
        aria-label="Search templates and steps"
        className="mb-2 w-full rounded-md border border-border px-2 py-1.5 text-sm"
      />
      {collapsible ? (
        // Reused from: templates/QaTemplateContentsPane.tsx (section chevron toggle)
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="mb-1 flex min-h-[44px] w-full items-center gap-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
          Location type
        </button>
      ) : (
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Location type</p>
      )}
      {shown ? <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {picker.groups.map((group) => (
          <button
            key={group.location_type || 'other'}
            type="button"
            onClick={() => picker.pickLocation(group.location_type)}
            className={`flex min-h-[44px] w-full flex-col items-start justify-center rounded-md px-2 text-left ${
              picker.locationType === group.location_type ? 'bg-muted' : 'hover:bg-muted/50'
            }`}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <LocationCheck
                  checked={picker.locationChecked(group.location_type)}
                  label={group.label}
                  onChange={(on) => picker.setLocationChecked(group.location_type, on)}
                />
                <span className="min-w-0 truncate text-sm font-medium">{group.label}</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">{group.count}</span>
                {picker.basketEntries.some(
                  (row) => (row.detail.location_type || '') === group.location_type,
                ) ? (
                  <span className="text-xs text-primary">✓</span>
                ) : null}
              </span>
            </span>
            {(picker.productDraft.locationNames[group.location_type] ?? []).length > 0 ? (
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                {joinProductNames(picker.productDraft.locationNames[group.location_type])}
              </span>
            ) : null}
          </button>
        ))}
      </div> : null}
    </div>
  )
}

function LocationCheck({
  checked,
  label,
  onChange,
}: {
  checked: boolean | 'mixed'
  label: string
  onChange: (on: boolean) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = checked === 'mixed'
  }, [checked])
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={`Select ${label}`}
      checked={checked === true}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 shrink-0"
    />
  )
}
